if (!customElements.get('aurrelle-discount-preview')) {
  customElements.define('aurrelle-discount-preview', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.input = this.querySelector('input');
      this.applyButton = this.querySelector('[data-apply]');
      this.status = this.querySelector('[role="status"]');
      this.result = this.querySelector('[data-result]');
      this.revision = 0;
      this.storageKey = 'aurrelle_product_discount';
      this.variants = JSON.parse(this.dataset.variants || '[]');
      this.syncPrice();
      this.applyButton.addEventListener('click', () => this.apply());
      this.input.addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); this.apply(); }
      });
      this.input.addEventListener('input', () => this.reset(true));
      this.querySelector('[data-remove]').addEventListener('click', () => { this.input.value = ''; this.reset(true); });
      this.onChange = event => {
        if (!event.target.matches('[name="quantity"], [name="id"], [name^="options"]')) return;
        const form = document.getElementById(this.dataset.formId);
        if (event.target.form !== form && !form?.contains(event.target) && !event.target.closest('product-info')) return;
        if (this.input.value.trim()) { clearTimeout(this.timer); this.invalidate(); this.timer = setTimeout(() => this.apply(), 200); } else { this.syncPrice(); }
      };
      document.addEventListener('change', this.onChange);
      const code = this.incomingCode();
      if (code) { this.input.value = code; this.apply(); }
    }
    disconnectedCallback() { document.removeEventListener('change', this.onChange); clearTimeout(this.timer); this.revision++; }
    selection() {
      const form = document.getElementById(this.dataset.formId);
      const data = form ? new FormData(form) : null;
      return { variant: String(data?.get('id') || this.dataset.variant), quantity: Math.max(1, Number(data?.get('quantity') || 1)) };
    }
    invalidate() {
      this.revision++;
      this.dataset.code = '';
      this.result.hidden = true;
      this.status.textContent = '';
      this.applyButton.disabled = false;
      this.input.removeAttribute('aria-invalid');
      this.syncPrice();
    }
    syncPrice(total) {
      const selection = this.selection();
      const variant = this.variants.find(item => String(item.id) === selection.variant);
      const base = variant ? Number(variant.price) / 100 : Number(this.dataset.basePrice);
      const price = total ?? base * selection.quantity;
      if (!Number.isFinite(price)) return;
      (this.closest('aurrelle-empty-cart') || document).querySelectorAll('[data-aurrelle-purchase-price], [data-lucine-buy-now] product-buy-price').forEach(target => {
        target.textContent = this.format(price, this.dataset.currency);
      });
      (this.closest('aurrelle-empty-cart') || document).querySelectorAll('[data-aurrelle-installments]').forEach(target => {
        const template = document.createElement('template');
        template.innerHTML = target.dataset.template;
        const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) walker.currentNode.textContent = walker.currentNode.textContent.replace('[amount]', this.format(price / 4, this.dataset.currency));
        target.replaceChildren(template.content.cloneNode(true));
      });
    }
    incomingCode() {
      const linkedCode = new URL(window.location.href).searchParams.get('discount');
      if (linkedCode?.trim()) return linkedCode.trim();
      const cookie = document.cookie.split(';').map(item => item.trim()).find(item => item.startsWith('discount_code='));
      if (cookie) {
        try {
          const code = decodeURIComponent(cookie.slice('discount_code='.length)).trim();
          if (code) return code;
        } catch (_) {}
      }
      try { return sessionStorage.getItem(this.storageKey) || ''; } catch (_) { return ''; }
    }
    rememberCode(code) {
      try {
        if (code) sessionStorage.setItem(this.storageKey, code);
        else sessionStorage.removeItem(this.storageKey);
      } catch (_) {}
      const value = encodeURIComponent(code);
      const expiry = code ? '' : '; Max-Age=0';
      const attributes = '; Path=/; SameSite=Lax' + (window.location.protocol === 'https:' ? '; Secure' : '') + expiry;
      document.cookie = 'discount_code=; Path=/; Max-Age=0; SameSite=Lax; Domain=' + window.location.hostname;
      document.cookie = 'discount_code=' + value + attributes;
    }
    reset(clearCode = false) {
      this.invalidate();
      try { sessionStorage.removeItem(this.storageKey); } catch (_) {}
      if (clearCode) {
        this.rememberCode('');
        const url = new URL(window.location.href);
        if (url.searchParams.has('discount')) {
          url.searchParams.delete('discount');
          window.history.replaceState(window.history.state, '', url);
        }
      }
    }
    format(amount, currency) { return new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency }).format(amount); }
    async apply() {
      const code = this.input.value.trim();
      this.reset();
      if (!code) { this.input.focus(); return; }
      const revision = this.revision;
      const selection = this.selection();
      this.status.textContent = this.dataset.checking;
      this.applyButton.disabled = true;
      const work = this.calculate(code, selection, revision);
      this.pending = work;
      await work;
      if (this.pending === work) this.pending = null;
    }
    async calculate(code, selection, revision) {
      const query = `mutation AurrelleDiscountPreview($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {
        cartCreate(input: $input) {
          cart {
            discountCodes { code applicable }
            cost { totalAmount { amount currencyCode } }
            lines(first: 1) { nodes { quantity cost { subtotalAmount { amount currencyCode } } } }
          }
          userErrors { message }
          warnings { message }
        }
      }`;
      try {
        const response = await fetch(this.dataset.endpoint, {
          method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(15000),
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ query, variables: {
            country: this.dataset.country,
            input: { buyerIdentity: { countryCode: this.dataset.country }, discountCodes: [code],
              lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + selection.variant, quantity: selection.quantity }] }
          } })
        });
        const body = await response.json();
        if (revision !== this.revision || !this.isConnected) return;
        if (!response.ok || body.errors?.length || body.data?.cartCreate?.userErrors?.length) throw new Error();
        const cart = body.data?.cartCreate?.cart;
        if (!cart) throw new Error();
        const accepted = cart.discountCodes.find(discount => discount.code.toLowerCase() === code.toLowerCase() && discount.applicable);
        if (!accepted) {
          this.input.setAttribute('aria-invalid', 'true');
          this.status.textContent = this.dataset.invalid;
          return;
        }
        const line = cart.lines.nodes[0];
        const money = cart.cost.totalAmount;
        const original = Number(line?.cost.subtotalAmount.amount);
        const total = Number(money.amount);
        if (!line || line.quantity !== selection.quantity || money.currencyCode !== this.dataset.currency || !Number.isFinite(total) || !Number.isFinite(original)) throw new Error();
        this.querySelector('[data-price]').textContent = this.format(total, money.currencyCode);
        const saved = Math.max(0, original - total);
        this.querySelector('[data-saving]').textContent = saved > 0 ? this.dataset.saved.replace('[amount]', this.format(saved, money.currencyCode)) : '';
        this.status.textContent = this.dataset.applied.replace('[code]', accepted.code);
        this.result.hidden = false;
        this.dataset.code = accepted.code;
        this.appliedSelection = selection;
        this.syncPrice(total);
        this.rememberCode(accepted.code);
      } catch (_) {
        if (revision === this.revision && this.isConnected) this.status.textContent = this.dataset.error;
      } finally {
        if (revision === this.revision) this.applyButton.disabled = false;
      }
    }
    async checkoutCode() {
      clearTimeout(this.timer);
      if (this.pending) await this.pending;
      const current = this.selection();
      if (this.dataset.code && (current.variant !== this.appliedSelection?.variant || current.quantity !== this.appliedSelection?.quantity)) await this.apply();
      return this.dataset.code || '';
    }
  });
}

